import { graphql } from "gatsby"
import { useMetadata } from "../data/use-metadata"
import Layout from "../components/layout"
import { GatsbySeo } from "gatsby-plugin-next-seo"
import React, { useEffect, useState, useRef } from "react"
import { Breadcrumb } from "../components/Breadcrumb"
import PropTypes from "prop-types"
import { GatsbyImage } from "gatsby-plugin-image"
import Link from "../components/Link"
import ReactMarkdown from "react-markdown"
import rehypeRaw from "rehype-raw"
import AuthorCard from "../components/AuthorCard"
import { Cta } from "../components/Cta"
import ReadMoreBasicCard from "../components/ReadMoreBasicCard"
import ReadMoreCard from "../components/ReadMoreCard"
import {
  FacebookIcon,
  FacebookMessengerIcon,
  FacebookMessengerShareButton,
  FacebookShareButton,
  LineIcon,
  LineShareButton,
  TelegramIcon,
  TelegramShareButton,
} from "react-share"
import EdmModal from "../components/modal/EdmModal"
import { Button } from "../components/Button"
import CategoryCard from "../components/CategoryCard"
import SubscribeForm from "../components/SubscribeForm"
import PostDirectory from "../components/PostDirectory"
import ReadingTime from "../components/ReadingTime"
import PostPopupModal from "../components/modal/PostPopupModal"
import { showPostPopup } from "../libs/function"
import DynamicImageViewer from "../components/DynamicImageViewer"

const PostTemplate = ({ data }) => {
  const {
    contentfulPost: post,
    allContentfulCategory: { nodes: categories },
  } = data

  // 取得contents文字內容
  const postContents = post.contents.reduce((accumulator, item) => {
    if (item.content) {
      accumulator += item.content?.content
    } else if (item.subContent) {
      accumulator += item.subContent?.subContent
    }
    return accumulator
  }, "")

  const { siteUrl } = useMetadata()
  const [isSuccessModal, setIsSuccessModal] = useState(false)
  const [isModal, setIsModal] = useState(false)

  useEffect(() => {
    showPostPopup(3, setIsModal)
  }, [])

  const canonical = `${siteUrl}/blog/${post.slug}`
  const crumbs = [
    {
      name: "首頁",
      url: "/",
    },
    {
      name: "Blog",
      url: `/blog`,
    },
    {
      name: post.name,
      url: `/blog/${post.slug}`,
    },
  ]

  // 將 contents 整理為段落群組

  //   原始的資料結構
  //     [
  //     {
  //       title:'',
  //       displayTitle: true
  //     }
  //     {
  //       title:'',
  //       displayTitle: false
  //     }
  // ]
  //   調整後的資料結構
  //     [
  //     {
  //       content: {...,displayTitle: true},
  //       subContent: [ {...},{...},{...}]
  //     },
  //     {
  //       content: {...,displayTitle: true},
  //       subContent: [ {...},{...},{...}]
  //     },
  //     ]
  const groupContents = contents => {
    const groupedData = []
    let currentGroup = null

    contents.forEach(item => {
      if (item.displayTitle) {
        // 如果當前有 currentGroup 且 displayTitle 為 true，則將當前組增加到 groupedData
        if (currentGroup) {
          groupedData.push(currentGroup)
        }
        // 建立新的 group
        currentGroup = { content: item, subContent: [] }
      } else {
        // 如果 item 不是 displayTitle 為 true 的，則將其添加到當前 currentGroup 的 subContent 中
        currentGroup.subContent.push(item)
      }
    })

    // 將最後一組加入 groupedData
    if (currentGroup) {
      groupedData.push(currentGroup)
    }

    return groupedData
  }

  const groupedContents = groupContents(post?.contents || [])
  console.log(groupedContents)
  const [tagName, setTagName] = useState(groupedContents[0].content.title)

  // 建立快速跳轉目錄 contentTags 資料
  const contentTags = groupedContents.map(group => {
    return { name: group.content.title }
  })

  // 收集要被觀察的所有元素
  const contentListsBlockRef = useRef(contentTags.map(() => React.createRef()))

  useEffect(() => {
    if (contentListsBlockRef.current) {
      let options = {
        threshold: 0,
        rootMargin: "-50% 0% -50% 0%", // 被觀察的元素滑到螢幕中間時觸發
      }

      const callback = entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            let target = entry.target
            let contentTags = target.getAttribute("data-contenttag") // 取得 data-attribute 的值

            setTagName(contentTags)
          }
        })
      }
      let observer = new IntersectionObserver(callback, options)

      contentListsBlockRef.current.forEach(element => {
        observer.observe(element.current)
      })
    }
  }, [contentListsBlockRef])

  const contents = groupedContents.map((group, index) => {
    const { content, subContent } = group

    return (
      <div
        key={`content_${index}`}
        className="markdown"
        ref={contentListsBlockRef.current[index]}
        data-contenttag={content.title}
      >
        {content.displayTitle && (
          <div id={`contentid-${content.id}`}>
            <h2 className="mt-12 mb-6">{content.title}</h2>
            {content.content?.content && (
              <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                {content.content.content}
              </ReactMarkdown>
            )}
          </div>
        )}
        {subContent.map((subItem, subIndex) => (
          <div key={subIndex}>
            <h3 className="mt-10 mb-6">{subItem.title}</h3>
            <ReactMarkdown rehypePlugins={[rehypeRaw]}>
              {subItem.subContent?.subContent}
            </ReactMarkdown>
          </div>
        ))}
      </div>
    )
  })

  // 取得同一小類最新一篇編輯精選文章
  const editorChoice = post.subCategory?.post
    .filter(item => item.editorsChoice && item.slug !== post.slug)
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, 1)

  // 取得同一小類熱門文章
  const subCategorPopularPost = post.subCategory?.post
    .filter(item => item.isPopularPost && item.slug !== post.slug)
    .sort((a, b) => b.updatedAt - a.updatedAt)

  // 取得同一小類最新文章
  const subCategoryPost = post.subCategory?.post
    .filter(item => item.slug !== post.slug)
    .sort((a, b) => b.updatedAt - a.updatedAt)

  // 取得同一大類非精選非熱門最新文章
  const categoryPost = post.subCategory?.category.subcategory
    .flatMap(item => item.post)
    .filter(item => !item.isPopularPost && !item.editorsChoice)
    .sort((a, b) => b.updatedAt - a.updatedAt)

  // 取得文章category的order資料
  const postcategory = post.subCategory?.category.order
  // 依文章category的order，取得其他category資料
  const orderMap = {
    1: [2, 3, 4],
    2: [1, 3, 5],
    3: [1, 5, 6],
    4: [1, 5, 6],
    5: [1, 2, 4],
    6: [1, 3, 5],
    7: [1, 2, 3],
  }
  const otherCategory = categories.filter(item =>
    orderMap[postcategory].includes(item.order)
  )

  // 其他主題區塊
  const otherList = otherCategory.map((item, index) => {
    return <CategoryCard key={index} data={item} />
  })

  // 延伸閱讀區塊資料
  const readingList = editorChoice
    .concat(subCategoryPost, categoryPost)
    .slice(0, 7)

  const realtedList = subCategorPopularPost.concat(subCategoryPost).slice(0, 3)

  // 延伸閱讀組件
  const RelatedPosts = ({ data }) => {
    const relatedPost = data.map((item, index) => (
      <Link to={`/blog/${item.slug}`} key={index}>
        <div className={`text-xl font-bold text-gray-500`}>{item.name}</div>
      </Link>
    ))
    return (
      <>
        <p className={`text-2xl font-bold text-gray-900 pt-4`}>相關文章</p>
        {relatedPost}
      </>
    )
  }
  RelatedPosts.propTypes = {
    data: PropTypes.array,
  }

  return (
    <Layout>
      {isModal && <PostPopupModal setIsModal={setIsModal} />}
      <GatsbySeo
        title={post.name}
        description={post?.excerpt?.excerpt}
        canonical={canonical}
        openGraph={{
          url: canonical,
          title: post.name,
          description: post?.excerpt?.excerpt,
          images: [
            {
              url: `https:${post.image.file.url}`,
              alt: post.name,
            },
          ],
          type: "article",
          article: {
            modifiedTime: post.updatedAt,
            tags: [post?.tags?.map(tag => tag.name)],
          },
        }}
      />

      <div className="my-6 sm:my-8 wrapper">
        <Breadcrumb crumbs={crumbs} />
      </div>

      {/*Header區塊*/}
      <div className="lg:relative">
        <div className="relative lg:static lg:container lg:mx-auto lg:px-8">
          {/*文章標題.首圖*/}
          <div className="grid grid-cols-6 relative overflow-hidden lg:static lg:container lg:mx-auto lg:pl-0">
            <div className="bg-white col-start-2 lg:col-start-1 col-span-5 px-6 pb-6 sm:px-12 sm:pb-12 mb-24 sm:mb-36 lg:mb-0 z-10">
              <h1 className="text-gray-900 text-3xl sm:text-6xl font-bold tracking-wide !leading-normal">
                {post.name}
              </h1>
            </div>
            <div className="block absolute left-0 h-full sm:hidden">
              <GatsbyImage
                alt={post.name}
                image={post.image.gatsbyImageData}
                loading="eager"
                className="h-full sm:hidden"
                imgStyle={{ objectFit: "cover" }}
              />
            </div>
            <div className="hidden sm:block absolute left-0 h-full lg:w-1/2">
              <GatsbyImage
                alt={post.name}
                image={post.image.gatsbyImageData}
                className="hidden sm:block w-full h-full"
                imgStyle={{ objectFit: "cover" }}
              />
            </div>
          </div>
          <div className="divide-y divide-gray-900 text-gray-900 container mx-auto px-6 mt-6 sm:px-8 lg:w-1/2 lg:pr-0 lg:pl-8 lg:mt-0 lg:ml-[50%]">
            {/*author.updateAt區塊*/}
            <div className="mb-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <div className="w-[2.75rem] h-[2.75rem]">
                    <DynamicImageViewer
                      alt={post.author.name}
                      image={post.author.image}
                      className={`rounded-full h-[44px] w-[44px]`}
                      style={{ borderRadius: "50%" }}
                    />
                  </div>
                  <p className="text-gray-900 text-lg font-bold">
                    {post.author.name}
                  </p>
                </div>
                <div
                  className={`text-right sm:flex sm:space-x-4 text-gray-900 `}
                >
                  <ReadingTime data={postContents} />
                  <p className="font-bold">{post.updatedAt}更新</p>
                </div>
              </div>
            </div>
            {/*subCategory.tags.excerpt區塊*/}
            <div className="pt-6">
              <div className="flex flex-col items-center sm:flex-row sm:justify-between mb-6 sm:mb-3 space-y-3 sm:space-y-0">
                <Link
                  to={`/blog/${post.subCategory.category.slug}/${post.subCategory.slug}`}
                  className="font-bold text-lg link-gradient max-w-max"
                >
                  {post.subCategory.name}
                </Link>
                {post?.tags && (
                  <div className="space-x-3 flex">
                    {post?.tags?.map(tag => {
                      return (
                        <p key={tag.id} className="leading-loose text-gray-500">
                          # {tag.name}
                        </p>
                      )
                    })}
                  </div>
                )}
              </div>
              <p className="text-lg !leading-loose">{post.excerpt.excerpt}</p>
            </div>
          </div>
        </div>
      </div>

      <div className={`wrapper lg:flex xl:grid xl:grid-cols-5 gap-8 my-16`}>
        {/* PostTemplate Toc */}
        <div className={`lg:relative`}>
          <PostDirectory
            customClass={`lg:sticky lg:top-16`}
            post={post}
            tagName={tagName}
            setTagName={setTagName}
          />
        </div>
        <div
          className={`space-y-24 xl:space-y-0 lg:w-full xl:grid xl:grid-cols-4 xl:col-span-4 gap-8`}
        >
          <div className={`xl:col-span-3`}>{contents}</div>
          <div className={`space-y-16`}>
            <div className={`space-y-4`}>
              <AuthorCard data={post} />
              {/*分享*/}
              <div className="flex items-center justify-center gap-2">
                <p>分享至</p>
                <FacebookShareButton url={canonical} quote={post.name}>
                  <FacebookIcon size={32} borderRadius={100} />
                </FacebookShareButton>
                <FacebookMessengerShareButton
                  url={canonical}
                  quote={post.name}
                  appId="423035109465194"
                >
                  <FacebookMessengerIcon size={32} borderRadius={100} />
                </FacebookMessengerShareButton>
                <TelegramShareButton url={canonical} quote={post.name} ml="2">
                  <TelegramIcon size={32} borderRadius={100} />
                </TelegramShareButton>
                <LineShareButton url={canonical} quote={post.name} ml="2">
                  <LineIcon size={32} borderRadius={100} />
                </LineShareButton>
              </div>
            </div>

            <div
              className={`space-y-12 sm:flex sm:space-x-16 sm:space-y-0 lg:space-x-0 lg:flex-col lg:space-y-16`}
            >
              {/*相關文章*/}
              <div className={`grid gap-4 border-t-4 border-t-yellow-300`}>
                <RelatedPosts data={realtedList} />
                <Link
                  to={`/blog/${post.subCategory.category.slug}/${post.subCategory.slug}`}
                >
                  <Button type={`solid`} size={`post_button`}>
                    更多文章
                  </Button>
                </Link>
              </div>
              {/* 訂閱區塊 */}
              <SubscribeForm
                onSuccess={() => setIsSuccessModal(true)}
                customClass={`xl:text-sm`}
              />
            </div>
          </div>
        </div>
      </div>

      {/*Cta*/}
      <Cta isEDM={post.cta.isEdm} className={post.cta.isEdm ? "" : "my-12"} />

      {/*其他主題*/}
      <div className={`wrapper`}>
        <div
          className={`text-center mt-24 mb-12 text-3xl font-bold sm:text-5xl`}
        >
          其他主題
        </div>
        <div className={`grid lg:grid-cols-3 gap-16`}>{otherList}</div>
        <div className={`text-center mt-16`}>
          <Link to={`/blog`}>
            <Button type={`solid`} size={`post_button`}>
              更多主題
            </Button>
          </Link>
        </div>
      </div>

      {/*延伸閱讀*/}
      <div className={`wrapper my-16`}>
        <div
          className={`text-center mt-24 mb-12 text-3xl font-bold sm:text-5xl`}
        >
          延伸閱讀
        </div>
        <div className="grid gap-16 sm:grid-cols-2 lg:grid-cols-3">
          {readingList.map((post, idx) => {
            if (idx === 0) {
              return (
                <div key={idx} className="sm:col-span-2 lg:col-span-3">
                  <ReadMoreCard data={post} lgGrid={true} />
                </div>
              )
            } else {
              return (
                <div key={idx}>
                  <ReadMoreBasicCard data={post} />
                </div>
              )
            }
          })}
        </div>
      </div>
      {isSuccessModal && <EdmModal setIsModal={setIsSuccessModal} />}
    </Layout>
  )
}

PostTemplate.propTypes = {
  data: PropTypes.object,
}
export default PostTemplate

export const pageQuery = graphql`
  query PostPage($slug: String!) {
    contentfulPost(slug: { eq: $slug }) {
      slug
      name
      updatedAt(formatString: "YYYY-MM-DD")
      createdAt(formatString: "YYYY-MM-DD")

      author {
        name
        title
        description {
          description
        }
        image {
          gatsbyImageData(
            placeholder: BLURRED
            quality: 100
            height: 88
            width: 88
          )
          file {
            url
          }
        }
      }
      cta {
        title
        link
        isEdm
      }
      excerpt {
        excerpt
      }
      image {
        gatsbyImageData(placeholder: BLURRED)
        file {
          url
        }
      }
      faqJsonLd {
        question
        answer {
          answer
        }
      }
      tags {
        id
        name
      }
      subCategory {
        name
        slug
        category {
          slug
          title
          order
          excerpt {
            excerpt
          }
          image {
            file {
              url
            }
          }
          subcategory {
            name
            slug
            post {
              id
              isPopularPost
              editorsChoice
              slug
              name
              updatedAt(formatString: "YYYY-MM-DD")
              excerpt {
                excerpt
              }
              author {
                name
                title
                description {
                  description
                }
                image {
                  gatsbyImageData(
                    placeholder: BLURRED
                    quality: 100
                    height: 88
                    width: 88
                  )
                }
              }
            }
          }
        }
        post {
          id
          editorsChoice
          isPopularPost
          slug
          name
          updatedAt(formatString: "YYYY-MM-DD")
          excerpt {
            excerpt
          }
          author {
            name
            title
            description {
              description
            }
            image {
              gatsbyImageData(
                placeholder: BLURRED
                quality: 100
                height: 88
                width: 88
              )
            }
          }
          image {
            gatsbyImageData(width: 768, height: 512, placeholder: BLURRED)
          }
        }
      }
      contents {
        ... on ContentfulContent {
          id
          title
          displayTitle
          content {
            content
          }
        }
        ... on ContentfulSubContent {
          id
          title
          subContent: content {
            subContent: content
          }
        }
      }
    }
    allContentfulCategory {
      nodes {
        slug
        title
        order
        excerpt {
          excerpt
        }
        image {
          file {
            url
          }
        }
      }
    }
  }
`
